import { useCallback, useEffect, useRef, useState } from 'react';

import type { AutocompleteOption } from '../../../types';

import { DEFAULT_OPTION } from '../utils/autocompleteDefault';

type AutocompleteSelectionProps = {
    options: AutocompleteOption[];
    showDefaultOption: boolean;
    onSelectOption: (opt: AutocompleteOption) => void;
    initial?: number;
};

export const useAutocompleteSelection = ({ options, showDefaultOption, onSelectOption, initial = -1 }: AutocompleteSelectionProps) => {
    const [selected, setSelected] = useState<number>(initial);
    const [hovered, setHovered] = useState<number | null>(null);

    const activeIndex = hovered ?? selected;
    const activeIndexRef = useRef(activeIndex);

    useEffect(() => {
        activeIndexRef.current = activeIndex;
    }, [activeIndex]);

    const resetSelection = useCallback(() => {
        setSelected(-1);
        setHovered(null);
    }, []);

    const clearHovered = useCallback(() => setHovered(null), []);

    const selectCurrentOption = useCallback(() => {
        const current = activeIndexRef.current;
        setSelected(current);
        setHovered(null);

        if (current === 0 && showDefaultOption) {
            onSelectOption(DEFAULT_OPTION);
            return DEFAULT_OPTION;
        }

        const offset = showDefaultOption ? 1 : 0;
        const index = current - offset;

        if (index >= 0 && index < options.length) {
            const opt = options[index];
            onSelectOption(opt);

            return opt;
        }

        return null;
    }, [showDefaultOption, options, onSelectOption]);

    return {
        setSelected,
        setHovered,
        clearHovered,
        activeIndex,
        activeIndexRef,
        resetSelection,
        selectCurrentOption,
    };
};
